import { useState, useEffect } from 'react';

import { useService, JOKI } from '../../JOKI';

/**
 * Get the device data by device code and start listening to changes in it.
 * @param {*} deviceCode
 */
export function useDevice(deviceCode) {
    const [targetCode, changeTargetDevice] = useState(deviceCode);
    const [data, setData] = useState(null);
    const [initialized, setDone] = useState(false);

    // Populate initial data
    useEffect(() => {
        if (targetCode !== null && data === null && initialized === false) {
            setDone(true);
            // console.log("Preloader for useDevice", targetCode, data, initialized);
            preFetch(targetCode);
        }
        // eslint-disable-next-line
    }, [targetCode, data, initialized]);

    // Listen for updates for this device.
    useEffect(() => {
        if (targetCode !== null) {
            return JOKI.on({
                from: 'DevicesService',
                key: 'deviceDataUpdated',
                fn: event => {
                    //if (event.key === "deviceDataUpdated") {

                    if (event.body !== undefined && event.body.code !== undefined && event.body.code === targetCode) {
                        // console.log(`HOOK:useDevice:event:${targetCode}: UPDATE `);
                        // console.log("update me", targetCode, event.body);
                        setData(event.body);
                    }
                    //}
                },
            });
        }
        if (data !== null) {
            setData(null);
        }
    }, [targetCode, data]);

    useEffect(() => {
        if (targetCode !== null) {
            return JOKI.on({
                from: 'DevicesService',
                key: 'DevicesWithAlertChanges',
                fn: event => {
                    if (Array.isArray(event.body)) {
                        if (event.body.findIndex(dev => dev.code === targetCode) >= 0) {
                            JOKI.trigger({
                                from: 'React:DevicesHooks:useDevice:afterAlert',
                                to: 'DevicesService',
                                key: 'forceDeviceUpdate',
                                body: {
                                    code: targetCode,
                                },
                            });
                        }
                    }
                },
            });
        }
    }, [targetCode]);

    function preFetch(fetchDeviceCode, tries = 0) {
        try {
            const data = JOKI.ask({
                to: 'DevicesService',
                key: 'getDevice',
                body: { code: fetchDeviceCode },
                syncAsk: true,
            });

            // console.log('The data',data)
            if (data.DevicesService.code === fetchDeviceCode) {
                // console.log("\n\nHOOK:useDevice:initializer results", data.DevicesService);
                setData(data.DevicesService);
            }
        } catch (err) {
            // If the device is not found try again after delay, as there might be fetch going on.
            if (tries < 5) {
                const nTries = tries + 1;
                setTimeout(() => {
                    preFetch(fetchDeviceCode, nTries);
                }, nTries * 5000);
            } else {
                console.error(`DevicesHooks:preFetch: Device code ${fetchDeviceCode} is unknown.`, err);
                setData({ error: true, msg: `Could not load data for device code ${fetchDeviceCode}` });
            }
        }
    }

    function changeTargetCode(newCode) {
        changeTargetDevice(newCode);
        if (newCode !== null) preFetch(newCode);
    }

    return [data, changeTargetCode]; // TODO: check where the second item in the list is used
}

/**
 * Get the currently selected device (show in DeviceDetails View) or change it.
 */
export function useSelectedDevice() {
    let state = 'INIT';

    const [selectedDeviceCode] = useService('DeviceDetailService');
    const [deviceData, change] = useDevice(selectedDeviceCode);

    const deviceCode = deviceData !== null && deviceData.code !== undefined ? deviceData.code : null;

    useEffect(() => {
        if (deviceCode !== selectedDeviceCode) {
            // console.log(`\nHOOK:useSelecedDevice: CHANGE DEVICE TO BE TRACKED FROM ${deviceCode} to ${selectedDeviceCode}\n\n`);
            change(selectedDeviceCode);
        }
        // eslint-disable-next-line
    }, [selectedDeviceCode]);

    if (selectedDeviceCode === null) {
        state = 'NOT';
    } else {
        if (selectedDeviceCode === deviceCode) {
            state = 'OK';
        }

        if (deviceCode === null) {
            state = 'LOADING';
        }

        if (selectedDeviceCode !== deviceCode && deviceCode !== null) {
            // console.log(`\nselected:\t${selectedDeviceCode}\ndeviceCode: \t${deviceCode}\nData\t\t${deviceData.code}\n\n`);
            state = 'CHANGING';
        }

        if (deviceData !== null && deviceData.error === true) {
            state = 'ERROR';
        }
    }

    function changeSelectedDevice(newDeviceCode) {
        // console.log("HOOK:useSelectedDevice:changeSelectedDevice to ", newDeviceCode);
        JOKI.trigger({
            from: 'REACT:useSelectedDevice:changeSelectedDevice',
            to: 'DeviceDetailService',
            key: newDeviceCode === selectedDeviceCode || newDeviceCode === null ? 'deselectDevice' : 'selectDevice',
            body: newDeviceCode,
        });
    }

    function reload() {
        JOKI.trigger({
            from: 'REACT:useSelectedDevice:reload',
            to: 'DevicesService',
            key: 'forceDeviceUpdate',
            body: {
                code: deviceCode,
            },
        });
    }

    return [deviceData, state, changeSelectedDevice, reload];
}
